import Phaser from "phaser";
import { COLORS, GAME_HEIGHT, GAME_WIDTH, PAUSE_BREATHE_BONUS } from "../config";
import { AudioFx } from "../systems/AudioFx";
import type { BreathMode } from "../types";

const BREATHS_REQUIRED = 3;
const INHALE_MS = 2200; // grow (in); the same again to shrink (out) via yoyo

/**
 * The breathing screen — the game's sacred mechanic. No microphone, no tapping:
 * a glowing circle simply breathes in and out three times on its own, then a
 * Continue button appears. Used to REVIVE after a fatal engulf or as a voluntary
 * pause-time BOOST. Meant to feel calm and intentional, never a punishment.
 */
export default class BreathingScene extends Phaser.Scene {
  private circle!: Phaser.GameObjects.Sprite;
  private ring!: Phaser.GameObjects.Arc;
  private counterText!: Phaser.GameObjects.Text;
  private promptText!: Phaser.GameObjects.Text;
  private breaths = 0;
  private finished = false;
  private mode: BreathMode = "revive";
  private canBoost = false;

  constructor() {
    super("Breathing");
  }

  create(data: { mode?: BreathMode; canBoost?: boolean }): void {
    this.mode = data.mode ?? "revive";
    this.canBoost = data.canBoost ?? false;
    this.breaths = 0;
    this.finished = false;

    this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x06101f, 0.9)
      .setDepth(0);

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2 - 40;

    this.circle = this.add
      .sprite(cx, cy, "soft")
      .setTint(COLORS.affirm)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(1);
    this.ring = this.add.circle(cx, cy, 120).setStrokeStyle(3, 0xeaf6ff, 0.5).setDepth(2);

    this.add
      .text(cx, cy - 300, this.mode === "revive" ? "Let's take a breath together" : "Take a moment to breathe", {
        fontFamily: "Arial, sans-serif",
        fontSize: "30px",
        color: "#eaf6ff",
      })
      .setOrigin(0.5)
      .setDepth(3);

    this.counterText = this.add
      .text(cx, cy, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "34px",
        color: "#0a1530",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    this.promptText = this.add
      .text(cx, cy + 300, "Just follow the circle — in as it grows, out as it shrinks.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "24px",
        color: "#9fd8ff",
        align: "center",
        wordWrap: { width: GAME_WIDTH - 120 },
      })
      .setOrigin(0.5)
      .setDepth(3);

    this.applyScale(0);
    this.playBreath();
  }

  /** One automated breath: grow (inhale) then shrink (exhale), then the next. */
  private playBreath(): void {
    this.breaths++;
    this.counterText.setText(`Breath ${this.breaths} of ${BREATHS_REQUIRED}`);
    AudioFx.breathTone(true);

    const proxy = { t: 0 };
    this.tweens.add({
      targets: proxy,
      t: 1,
      duration: INHALE_MS,
      yoyo: true, // grow then shrink = one full breath
      ease: "Sine.easeInOut",
      onUpdate: () => this.applyScale(proxy.t),
      onComplete: () => {
        if (this.breaths >= BREATHS_REQUIRED) this.showContinue();
        else this.playBreath();
      },
    });
  }

  /** t in [0..1]: 0 = fully exhaled (small), 1 = fully inhaled (large). */
  private applyScale(t: number): void {
    const s = 0.7 + t * 0.8;
    this.circle.setScale(s * 1.4);
    this.ring.setScale(s);
  }

  private showContinue(): void {
    this.counterText.setText(
      this.mode === "boost" && this.canBoost ? `+${PAUSE_BREATHE_BONUS} Strength` : "Welcome back",
    );
    this.promptText.setText("");

    const cx = GAME_WIDTH / 2;
    const y = GAME_HEIGHT / 2 + 220;
    const bg = this.add
      .rectangle(cx, y, 320, 72, 0x1f4f7a, 1)
      .setStrokeStyle(2, 0x9fd8ff, 0.8)
      .setDepth(5)
      .setInteractive({ useHandCursor: true });
    const label = this.add
      .text(cx, y, "Continue", {
        fontFamily: "Arial, sans-serif",
        fontSize: "28px",
        color: "#eaf6ff",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(6);

    // Gentle attention pulse on the button.
    this.tweens.add({ targets: [bg, label], scale: 1.05, duration: 700, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });

    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => bg.setFillStyle(0x2a6699));
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => bg.setFillStyle(0x1f4f7a));
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this.complete());
  }

  private complete(): void {
    if (this.finished) return;
    this.finished = true;
    AudioFx.flourish();
    this.tweens.add({
      targets: [this.circle, this.ring],
      scale: 2.4,
      alpha: 0,
      duration: 500,
      ease: "Sine.easeIn",
      onComplete: () => {
        this.scene.resume("Game"); // fires RESUME on Game → revive / boost
        this.scene.stop();
      },
    });
  }
}
